import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import useApi from '../../hooks/useApi'
import useTranslation, { importJSON, tState } from '../../hooks/useTranslation'
import texts from '../../model/text'
import extractSlug from '../../utils/extractSlug'
import invalidateListQueries from '../../utils/invalidateListQueries'
import { exportData } from '../../../../common/model/export/exportData.js'
import { formatData } from '../../../../common/model/export/formatData.js'
import { importFormatsFor } from '../../../../common/model/export/importData.js'

/**
 * Runs the import plan phase (task 35.02 M1).
 *
 * Plans only — nothing here writes. The file is held by the dialog rather than
 * uploaded to a server-side staging area, so the apply phase (M2) can re-post
 * the same bytes without asking the user to pick the file again (§11.1).
 */

/**
 * The BCP-47 tag an export puts at the end of its name — the client half of the
 * same rule the server applies (35.02 gap 2).
 */
const localeFromFilename = (name = '') => {
    const match = String(name).match(/-([a-z]{2}-[A-Z]{2})\.[A-Za-z0-9]+$/)
    return match ? match[1] : null
}

/**
 * Load the translation catalogue for a locale that is not the user's own.
 *
 * A Danish export carries Danish column headers, and matching them needs the
 * Danish catalogue — the importer's own language says nothing about a file
 * someone else produced. The catalogues for every locale already ship with the
 * client, and `importJSON` is what the app uses to load them, so this only has
 * to ask for a different language than usual.
 *
 * Same module list the current area uses, because that is where the labels a
 * list can export live.
 */
const loadCatalogue = async (locale) => {
    const slug = extractSlug(window.location.pathname)
    const paths = texts[slug] || []

    const loaded = await Promise.allSettled(paths.map(path => importJSON(path, locale)))

    return loaded.reduce((catalogue, result) => (
        result.status === 'fulfilled' ? { ...catalogue, ...result.value } : catalogue
    ), {})
}

const useListImport = (endpoint, entity) => {
    const { postMeForm } = useApi()
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const [isPlanning, setIsPlanning] = useState(false)

    /* Formats that have a reader, straight from the shared registry — so the
     * file filter follows the model rather than being hardcoded here. */
    const allowed = importFormatsFor(entity)
    const importFormats = Object.entries(formatData)
        .filter(([id, format]) => format.importable && (!allowed || allowed.includes(id)))
        .map(([id, format]) => ({ id, extension: format.extension, label: format.label }))
    const [isApplying, setIsApplying] = useState(false)

    /**
     * Header text → field name, the inverse of what export sends.
     *
     * An exported CSV carries translated headers, and the server has no
     * translations, so the client has to say what they meant. Built from the
     * same profiles export writes with, which is what makes a round trip line up.
     */
    /**
     * Header text -> field name, the inverse of what export sends.
     *
     * `catalogue` is supplied when the file was written in another language: the
     * labels are then looked up directly in that language's catalogue instead of
     * through t(), which only knows the user's own. Without it a Danish file
     * read by an English user matches nothing and every column is reported as
     * ignored.
     */
    const buildHeaderMap = useCallback((profileName = 'default', catalogue = null) => {
        const profiles = exportData[entity] || {}
        const map = {}
        const label = (key) => (catalogue ? catalogue[key] : t(key))

        // Every profile, not just the chosen one: the file may have come from a
        // different profile than the one currently selected, and a header that
        // resolves is better than a column reported as skipped.
        for (const profile of Object.values(profiles)) {
            for (const field of profile.fields || []) {
                const text = field.label && label(field.label)
                if (text) map[text] = field.field
            }
        }

        // The active profile wins on any collision.
        for (const field of profiles[profileName]?.fields || []) {
            const text = field.label && label(field.label)
            if (text) map[text] = field.field
        }

        return map
    }, [entity, t])

    /**
     * The header map for a file, in whatever language the file declares.
     *
     * The locale comes from the filename, so this costs nothing when the file is
     * the user's own language — which is the ordinary case.
     */
    const headersForFile = useCallback(async (file, profile) => {
        const fileLocale = localeFromFilename(file?.name)
        const own = tState.currentLang

        if (!fileLocale || fileLocale === own) return buildHeaderMap(profile)

        const catalogue = await loadCatalogue(fileLocale)
        return Object.keys(catalogue).length > 0
            ? buildHeaderMap(profile, catalogue)
            : buildHeaderMap(profile)
    }, [buildHeaderMap])

    /**
     * A readable name for a field in the plan, from whichever profile declares
     * it. The plan itself stays language-neutral — it carries field names, and
     * the translation happens here, where the catalogue is.
     *
     * Falls back to the field name: a schema-derived import (Tier 1) has no
     * declared labels at all, and `netSalesPrice` beats showing nothing.
     */
    const fieldLabel = useCallback((field) => {
        for (const profile of Object.values(exportData[entity] || {})) {
            const declared = profile.fields?.find(candidate => candidate.field === field)
            if (declared?.label) return t(declared.label)
        }
        return field
    }, [entity, t])

    const planImport = useCallback(async ({ file, profile = 'default', useNaturalKey = false } = {}) => {
        if (!file) return { error: { error: 'import.error.noFile' } }

        setIsPlanning(true)
        try {
            const response = await postMeForm(`${endpoint}/import/plan`, file, {
                format: file.name?.toLowerCase().endsWith('.json') ? 'json' : 'csv',
                headers: JSON.stringify(await headersForFile(file, profile)),
                useNaturalKey: String(useNaturalKey),
                options: JSON.stringify({
                    locale: navigator.language || 'en-US',
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                })
            })

            /*
             * postMeForm returns the whole axios response when it succeeds, but
             * the server's error body when it fails. Unwrap whichever arrived.
             */
            const payload = response?.data ?? response

            if (!payload || payload.status === 'error' || payload.error) {
                return { error: payload || { error: 'import.error.failed' } }
            }

            return { plan: payload }

        } catch (error) {
            console.error('Import plan error:', error.message)
            return { error: { error: error.message } }
        } finally {
            setIsPlanning(false)
        }
    }, [endpoint, headersForFile, postMeForm])

    /**
     * Write the approved part of a plan.
     *
     * The same file goes up again — the dialog has been holding it — because the
     * server takes every value from the file and nothing from us (§7). All we
     * send is which rows and fields were approved, plus the fingerprints we were
     * shown, so the server can tell whether anything moved underneath.
     */
    const applyImport = useCallback(async ({ file, approvals, profile = 'default', useNaturalKey = false } = {}) => {
        if (!file) return { error: { error: 'import.error.noFile' } }
        if (!approvals?.length) return { error: { error: 'import.error.nothingApproved' } }

        setIsApplying(true)
        try {
            const response = await postMeForm(`${endpoint}/import/apply`, file, {
                format: file.name?.toLowerCase().endsWith('.json') ? 'json' : 'csv',
                headers: JSON.stringify(await headersForFile(file, profile)),
                useNaturalKey: String(useNaturalKey),
                approvals: JSON.stringify(approvals),
                options: JSON.stringify({
                    locale: navigator.language || 'en-US',
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                })
            })

            const payload = response?.data ?? response

            if (!payload || payload.status === 'error' || payload.error) {
                return { error: payload || { error: 'import.error.failed' } }
            }

            await invalidateListQueries(endpoint, queryClient)
            return { report: payload }

        } catch (error) {
            console.error('Import apply error:', error.message)
            return { error: { error: error.message } }
        } finally {
            setIsApplying(false)
        }
    }, [endpoint, headersForFile, postMeForm, queryClient])

    return { planImport, applyImport, isPlanning, isApplying, importFormats, fieldLabel }
}

export default useListImport
